// MCQ pre-course assessment. RCUK-style 10-question randomised set, 75% pass threshold.
// Each question shows options; learner picks; submit at the end produces a scored result with
// per-question explanations and a pass/fail headline.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

// Map each MCQ `topic` to the chapter / OCR file(s) so failing learners can read up.
// The OCR pages live under rag-sources/ocr/ — not web-served, but the chapter
// reference doubles as a guideline citation pointer.
const TOPIC_REFERENCE: Record<string, { chapter: string; ocr_files: string; resus_org_uk?: string }> = {
  algorithm_shockable: { chapter: 'Adult ALS algorithm (shockable arm)', ocr_files: 'A-06, B-20..24', resus_org_uk: 'https://www.resus.org.uk/library/2021-resuscitation-guidelines/adult-advanced-life-support-guidelines' },
  algorithm_non_shockable: { chapter: 'Adult ALS algorithm (non-shockable arm)', ocr_files: 'A-06, B-20..24', resus_org_uk: 'https://www.resus.org.uk/library/2021-resuscitation-guidelines/adult-advanced-life-support-guidelines' },
  cpr_quality: { chapter: 'CPR quality — rate, depth, recoil, CCF', ocr_files: 'B-22..24', resus_org_uk: 'https://www.resus.org.uk/library/2021-resuscitation-guidelines/adult-advanced-life-support-guidelines' },
  rhythm_recognition: { chapter: 'Rhythm recognition (Ch 8)', ocr_files: 'C-14..19' },
  reversible_causes: { chapter: '4Hs / 4Ts — reversible causes', ocr_files: 'C-04, C-08' },
  peri_arrest: { chapter: 'Peri-arrest arrhythmias (Ch 11)', ocr_files: 'D-11..15', resus_org_uk: 'https://www.resus.org.uk/library/2021-resuscitation-guidelines/adult-advanced-life-support-guidelines' },
  post_rosc: { chapter: 'Post-resuscitation care (Ch 13)', ocr_files: 'E-10..16', resus_org_uk: 'https://www.resus.org.uk/library/2021-resuscitation-guidelines/post-resuscitation-care-guidelines' },
  special_circumstances: { chapter: 'Special circumstances (Ch 12)', ocr_files: 'D-16..24, E-01..09', resus_org_uk: 'https://www.resus.org.uk/library/2021-resuscitation-guidelines/special-circumstances-guidelines' },
  airway: { chapter: 'Airway management & ventilation (Ch 7)', ocr_files: 'C-09..13' },
  defibrillation: { chapter: 'Defibrillation (Ch 9)', ocr_files: 'C-20..24, D-01..04' },
  pacing: { chapter: 'Cardiac pacing (Ch 10)', ocr_files: 'D-05..10' },
  drug_dosing: { chapter: 'Drugs — Appendices A & B', ocr_files: 'F-08..F-10' },
  non_tech: { chapter: 'Non-technical skills (Ch 2)', ocr_files: 'A-08..11' },
  pre_arrest: { chapter: 'Recognising deterioration & NEWS2 (Ch 3)', ocr_files: 'A-12..24' },
  decisions: { chapter: 'Decisions about CPR (Ch 16)', ocr_files: 'E-23..24, F-01..05' },
};

interface Question { id: string; topic: string; stem: string; options: string[]; }
interface Result {
  total: number; correct: number; score_pct: number; pass_threshold_pct: number; passed: boolean;
  attempted_at: number;
  breakdown: Array<{ question_id: string; stem: string; options: string[]; chosen_index: number; correct_index: number; correct: boolean; explanation_md: string; topic: string; }>;
}

export default function MCQ() {
  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [region, setRegion] = useState<string>('');
  const [passThreshold, setPassThreshold] = useState<number>(75);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    api.mcqStart()
      .then((r) => { setQuestions(r.questions); setRegion(r.region); setPassThreshold(r.pass_threshold_pct); })
      .catch((e) => setErr(e.message || 'Failed to load MCQ'));
    api.mcqHistory().then((r) => setHistory(r.sessions)).catch(() => {});
  }, []);

  const allAnswered = useMemo(() => questions != null && questions.every((q) => answers[q.id] !== undefined), [questions, answers]);

  async function submit() {
    if (!questions || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.mcqSubmit(questions.map((q) => ({ question_id: q.id, chosen_index: answers[q.id]! })));
      setResult(r);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setErr(e.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  }

  if (err) return <div className="max-w-3xl mx-auto px-4 py-10 text-alarm-red">{err}</div>;
  if (!questions) return <div className="max-w-3xl mx-auto px-4 py-10 text-mono-white/60">Loading questions…</div>;

  // Result screen
  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/dashboard" className="text-mono-white/50 hover:text-mono-white text-sm">← Back to dashboard</Link>
        <h1 className="text-2xl font-semibold mt-3 mb-2">Pre-course MCQ — results</h1>
        <div className={`panel p-6 mb-6 border-l-4 ${result.passed ? 'border-mono-green' : 'border-alarm-red'}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-mono-white/40">Score</div>
              <div className={`text-4xl font-mono tabular-nums ${result.passed ? 'text-mono-green' : 'text-alarm-red'}`}>
                {result.score_pct}<span className="text-base text-mono-white/40 ml-1">/ 100</span>
              </div>
              <div className="text-sm text-mono-white/60 mt-1">{result.correct} of {result.total} correct · pass threshold {result.pass_threshold_pct}%</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-mono-white/40">Outcome</div>
              <div className={`text-2xl font-semibold ${result.passed ? 'text-mono-green' : 'text-alarm-red'}`}>{result.passed ? '✓ Pass' : '✖ Fail'}</div>
            </div>
          </div>
        </div>

        <h2 className="text-mono-white/60 uppercase tracking-widest text-xs mb-3">Per-question breakdown</h2>
        <div className="space-y-4">
          {result.breakdown.map((b, i) => (
            <div key={b.question_id} className={`panel p-5 ${b.correct ? '' : 'border-l-4 border-alarm-red'}`}>
              <div className="flex justify-between items-start gap-3 mb-2">
                <p className="font-medium">{i + 1}. {b.stem}</p>
                <span className={b.correct ? 'text-mono-green text-xs font-semibold' : 'text-alarm-red text-xs font-semibold'}>{b.correct ? '✓ Correct' : '✖ Wrong'}</span>
              </div>
              <div className="space-y-1 text-sm">
                {b.options.map((opt, j) => {
                  const isChosen = j === b.chosen_index;
                  const isCorrect = j === b.correct_index;
                  const cls = isCorrect ? 'bg-mono-green/15 text-mono-green ring-1 ring-mono-green/40'
                    : isChosen ? 'bg-alarm-red/15 text-alarm-red ring-1 ring-alarm-red/40'
                    : 'bg-ink-800 text-mono-white/60';
                  return <div key={j} className={`px-3 py-2 rounded ${cls}`}>{String.fromCharCode(65 + j)}. {opt}</div>;
                })}
              </div>
              <p className="text-sm text-mono-white/85 mt-3 italic">{b.explanation_md}</p>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <span className="text-[10px] text-mono-white/30 uppercase tracking-widest">{b.topic}</span>
                {TOPIC_REFERENCE[b.topic] && (
                  <span className="text-[10px] text-mono-white/40">
                    📖 <span className="text-mono-white/60">{TOPIC_REFERENCE[b.topic].chapter}</span>
                    <span className="text-mono-white/30 ml-1">· OCR: {TOPIC_REFERENCE[b.topic].ocr_files}</span>
                    {TOPIC_REFERENCE[b.topic].resus_org_uk && (
                      <a href={TOPIC_REFERENCE[b.topic].resus_org_uk} target="_blank" rel="noreferrer" className="ml-2 text-brand-600 hover:underline">resus.org.uk ↗</a>
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-3 flex-wrap">
          <a
            href={api.mcqCertificateUrl(result.attempted_at)}
            download
            className="btn-primary"
            title={result.passed ? 'PDF certificate of MCQ completion (pass)' : 'PDF record of attempt (not yet a pass)'}
          >
            ⤓ {result.passed ? 'Download certificate (PDF)' : 'Download attempt record (PDF)'}
          </a>
          <button onClick={() => { setResult(null); setAnswers({}); api.mcqStart().then((r) => setQuestions(r.questions)); }} className="btn-ghost">Take again with fresh questions</button>
          <Link to="/dashboard" className="btn-ghost">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  // Taking the test
  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to="/dashboard" className="text-mono-white/50 hover:text-mono-white text-sm">← Back to dashboard</Link>
      <div className="flex justify-between items-start mt-3 mb-2 flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">Pre-course MCQ — Adult ALS</h1>
        <span className="chip-off text-[10px] uppercase tracking-widest">{region}</span>
      </div>
      <p className="text-mono-white/60 text-sm mb-6">
        {questions.length} randomised questions. <strong>{passThreshold}% to pass</strong> (RCUK standard). No time limit — work through deliberately. Submit at the end to see explanations.
      </p>

      {history.length > 0 && (
        <div className="panel p-3 mb-6 bg-ink-800/50">
          <div className="text-xs text-mono-white/60 mb-1 uppercase tracking-widest">Your previous attempts</div>
          <div className="flex flex-wrap gap-2">
            {history.slice(0, 6).map((s) => (
              <a
                key={s.attempted_at}
                href={api.mcqCertificateUrl(s.attempted_at)}
                download
                title={`Download PDF · ${s.correct}/${s.total} · ${s.passed ? 'PASS' : 'fail'}`}
                className={`chip ${s.passed ? 'bg-mono-green/15 text-mono-green' : 'bg-alarm-red/15 text-alarm-red'} text-[11px] hover:underline`}
              >
                {new Date(s.attempted_at).toLocaleDateString()} · {s.score_pct}% ⤓
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {questions.map((q, i) => (
          <div key={q.id} className="panel p-5">
            <p className="font-medium mb-3">{i + 1}. {q.stem}</p>
            <div className="space-y-2">
              {q.options.map((opt, j) => {
                const selected = answers[q.id] === j;
                return (
                  <button
                    key={j}
                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: j }))}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${selected ? 'bg-brand-600 text-white' : 'bg-ink-800 hover:bg-ink-950 text-mono-white/85'}`}
                    aria-pressed={selected}
                  >
                    <span className="font-mono mr-2">{String.fromCharCode(65 + j)}.</span>{opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 mt-8 panel p-4 flex justify-between items-center bg-ink-900/95 backdrop-blur shadow-xl">
        <div className="text-sm text-mono-white/70">
          {Object.keys(answers).length} / {questions.length} answered
        </div>
        <button
          onClick={submit}
          disabled={!allAnswered || submitting}
          className="btn-primary disabled:opacity-50"
        >
          {submitting ? 'Marking…' : allAnswered ? 'Submit and reveal score' : `Answer all ${questions.length} to submit`}
        </button>
      </div>
    </div>
  );
}
